import { parseEncodedObject, updateAddBothToCartButton } from './ss-utils.js';
import { BaseCard } from './ss-BaseCard.js';

class BundleCard extends BaseCard {
  constructor(containerId) {
    super(containerId);
  }

  renderSkeleton() {
    const skeleton = document.createElement('div');
    skeleton.classList.add('bundle-card', 'skeleton');
    skeleton.innerHTML = `
      <div class="card-image">
        <div class="card-image-skeleton"></div>
      </div>
      <div class="card-content">
        <div class="card-title-skeleton"></div>
        <div class="card-price-skeleton"></div>
        <div class="card-select-skeleton"></div>
      </div>
    `;
    this.container.appendChild(skeleton);
  }

  formatPrice(price) {
    return String(price)?.split('.')?.[1]?.length === 1 ? String(price) + '0' : String(price);
  }

  getProductNameAndColor(name) {
    const [productName, productColor] = name.split(' - ');
    return {
      productName,
      productColor: productColor ? `<span class="title-color">${productColor}</span>` : ''
    };
  }

  formatImages(images, imageAlts, productName) {
    const imagesArr = images?.split('|');
    return imagesArr?.map((image, index) => ({
      url: image.replace(/(\.[^.]+)$/, '_600x600$1'),
      alt: imageAlts[index] || productName
    }));
  }

  createVariantSection(variants, productId) {
    if (!variants?.length) return '';

    const mainProductFieldset = document.querySelector('fieldset.product-form__input');
    const mainProductSelectedSize = mainProductFieldset?.querySelector('input[type="radio"]:checked')?.value;
    const matchingVariant = variants.find(variant =>
      variant.title === mainProductSelectedSize && variant.inventory_quantity > 0
    );

    return `
      <div class="variant-selector">
        <select id="variant-selector-${productId}" class="variant-select" data-product-id="${productId}">
        ${variants.length === 1 ? `
          <option value="${variants[0].id?.split('/').pop()}" ${variants[0].inventory_quantity <= 0 ? 'disabled' : 'selected'}>
            One Size
          </option>
          ` : `
          <option value="" disabled ${!matchingVariant ? 'selected' : ''}>Select Size</option>
          ${variants.map((variant, index) => {
            const id = variant.id.split('/').pop();
            const disabled = variant.inventory_quantity <= 0 ? 'disabled' : '';
            const selected = matchingVariant && variant.title === matchingVariant.title ? 'selected' : '';
            return `<option value="${id}" ${disabled} ${selected}>${variant.title}${variant.inventory_quantity <= 0 ? ' - Sold Out' : ''}</option>`;
          }).join('')}
          `}
        </select>
      </div>
    `;
  }

  createPriceSection(msrpPrice, price, productId) {

    const formattedMsrp = this.formatPrice(msrpPrice);
    const formattedPrice = this.formatPrice(price);

    if (!msrpPrice || Number(formattedMsrp) <= Number(formattedPrice)) {
      return `
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedPrice} USD
          </span>
        </div>
      `;
    }

    return `
      <div class="price--on-sale price__sale">
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedMsrp} USD
          </span>
        </span>
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="price-item price-item--sale price-item--last" data-product-id="${productId}">
          $${formattedPrice} USD
        </span>
      </div>
    `;
  }

  createCardBadge(product, variants) {
    const isSoldOut = product.ss_available === 0 || (variants?.length > 0 && variants?.every(variant => variant.inventory_quantity <= 0));

    if (isSoldOut) {
      return `
        <span class="badge badge--bottom-left color-inverse">
          Sold Out
        </span>
      `;
    }

    if (product.ss_on_sale) {
      return `
        <span class="badge badge--bottom-left color-accent-2">
          Sale
        </span>
      `;
    }

    return '';
  }

  setupEventListeners(bundleCard, product) {
    const select = bundleCard.querySelector('.variant-select');
    const addBothButton = bundleCard.querySelector('.add-both-to-cart');

    if (select && addBothButton) {
      updateAddBothToCartButton(addBothButton, select.value);

      select.addEventListener('change', (event) => {
        updateAddBothToCartButton(addBothButton, event.target.value);
      });
    }

    if (addBothButton) {
      this.setupAddToCartHandler(addBothButton, bundleCard);
    }
  }

  async setupAddToCartHandler(button, bundleCard) {
    button.addEventListener('click', async (event) => {
      const select = bundleCard.querySelector('.variant-select');
      const variantId = select?.value;
      const mainProductForm = document.querySelector('form[action="/cart/add"]');
      const mainProductVariantId = mainProductForm?.querySelector('input[name="id"]')?.value;

      if (!variantId || !mainProductVariantId) return;

      await this.handleAddToCart(button, variantId, mainProductVariantId);
    });
  }

  async handleAddToCart(button, variantId, mainProductVariantId) {
    this.setButtonLoadingState(button, true);

    try {
      const isSingleItem = button.dataset.singleItem === 'true';
      const items = isSingleItem ?
        [{ id: variantId, quantity: 1 }] :
        [
          { id: mainProductVariantId, quantity: 1 },
          { id: variantId, quantity: 1 }
        ];

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add items to cart');

      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }

      this.showMessage(button, 'Items added to cart', 'success');
    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showMessage(button, 'Failed to add items to cart', 'error');
    } finally {
      this.setButtonLoadingState(button, false);
    }
  }

  setButtonLoadingState(button, isLoading) {
    button.classList.toggle('loading', isLoading);
    button.querySelector('.loading-overlay__spinner').classList.toggle('hidden', !isLoading);
    button.disabled = isLoading;
  }

  showMessage(button, message, type) {
    const messageElement = document.createElement('div');
    messageElement.classList.add(`${type}-message`);
    messageElement.textContent = message;
    button.parentNode.appendChild(messageElement);

    setTimeout(() => messageElement.remove(), 2000);
  }

  render(product, clearContainer = false) {
    if (clearContainer) {
      this.clearContainer();
    }

    if (!product) return;

    const variants = parseEncodedObject(product.variants);
    variants.forEach(variant => {
      variant.available = variant.inventory_quantity > 0;
    });

    const { productName, productColor } = this.getProductNameAndColor(product.name);
    const images = this.formatImages(product.images, product.images_alt?.split('|'), product.name);
    const variantSection = this.createVariantSection(variants, product.uid);
    const priceSection = this.createPriceSection(product.msrp, product.price, product.uid);
    const cardBadge = this.createCardBadge(product, variants);

    const bundleCard = document.createElement('div');
    bundleCard.classList.add('bundle-card');
    bundleCard.dataset.product = JSON.stringify(product);

    bundleCard.innerHTML = `
      <div class="card-wrapper">
        <div class="card card--product card--outline" tabindex="-1">
          <div class="card__inner">
            <div class="media media-grey-background media--adapt media--hover-effect" style="padding-bottom: 100.0%;">
              <img src="${images[0].url}" alt="${images[0].alt}" loading="lazy" class="motion-reduce main-product-image" width="600" height="600">
              ${images[1] && images[1].url !== images[0].url ? `<img src="${images[1].url}" alt="${images[1].alt}" loading="lazy" class="motion-reduce secondary-product-image" width="600" height="600">` : ''}
            </div>
            <div class="card__badge">
              ${cardBadge}
            </div>
          </div>
        </div>
        <div class="card-information">
          <div class="card-information__wrapper">
            <h3 class="card-information__text h5">
              <a href="/products/${product.handle}" class="full-unstyled-link" onmousedown="IntelliSuggest.clickItem(this, { siteId: '40t3km', intellisuggestData: '${product.intellisuggestData}', intellisuggestSignature: '${product.intellisuggestSignature}' })">${productName}${productColor}</a>
            </h3>
            <span class="caption-large light"></span>

            <div class="price">
              <div>
                ${priceSection}
                <small class="unit-price caption hidden">
                  <span class="visually-hidden">Unit price</span>
                  <span class="price-item price-item--last">
                    <span></span>
                    <span>/</span>
                    <span class="visually-hidden">&nbsp;per&nbsp;</span>
                    <span></span>
                  </span>
                </small>
              </div>
            </div>
            ${variantSection}
            <div class="product-form__buttons">
              <button
                id="add-both-to-cart"
                type="button"
                class="product-form__submit button button--full-width button--primary add-both-to-cart"
                data-product-id="${product.uid}">
                <span>${variants?.length > 1 ? 'Select a Size' : 'Add Both to Cart'}</span>
                <div class="loading-overlay__spinner hidden">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.container.appendChild(bundleCard);
    this.setupEventListeners(bundleCard, product);
  }
}

export default BundleCard;